import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatMap, filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, switchMap } from 'rxjs';
import { selectAttributeListTabs, selectAttributeListVisible } from '../state/attribute-list.selectors';
import { changeAttributeListTabs } from '../state/attribute-list.actions';
import { AttributeListTabModel } from '../models/attribute-list-tab.model';
import { nanoid } from 'nanoid';
import { AttributeListDataModel } from '../models/attribute-list-data.model';
import {
  BoundsModel,
  FeaturesResponseModel, LayerExtractCapabilitiesModel, LayerExtractResponseModel, UniqueValuesResponseModel,
} from '@tailormap-viewer/api';
import { DEFAULT_ATTRIBUTE_LIST_CONFIG } from '../models/attribute-list-config.model';
import { AttributeListSourceModel, TabModel } from '../models/attribute-list-source.model';
import {
  CanCheckRowsParams,
  CanExpandRowParams, CheckedRowsChangedParams, DownloadLayerExtractParams, DownloadLayerExtractResponse,
  FeatureDetailsModel, GetFeatureDetailsParams, GetLayerExtractCapabilitiesParams, GetLayerExtractParams,
  GetStatisticParams, GetStatisticResponse, GetUniqueValuesParams, ZoomToExtentBoundsParams,
} from '../models/attribute-list-api-service.model';
import { GetFeaturesParams } from '../../../models/get-features-param.model';

interface TabModelWithTabSourceId extends TabModel {
  tabSourceId: string;
}

interface TabFromLayerResult {
  tab: AttributeListTabModel;
  data: AttributeListDataModel[];
}

@Injectable({
  providedIn: 'root',
})
export class AttributeListManagerService implements OnDestroy {

  private store$ = inject(Store);

  private sources$ = new BehaviorSubject<AttributeListSourceModel[]>([]);
  private tabsFromSources$ = this.sources$.asObservable()
    .pipe(
      switchMap(sources => {
        if (sources.length === 0) {
          return of([]);
        }
        return combineLatest(sources.map(s => {
          return s.tabs$.pipe(map(tabs => tabs.map(tab => ({
            ...tab,
            tabSourceId: s.id,
          }))));
        })).pipe(map(tabs => tabs.flat()));
      }),
    );

  public static readonly EMPTY_ATTRIBUTE_LIST_TAB: AttributeListTabModel = {
    id: '',
    label: '',
    layerId: '',
    selectedDataId: '',
    initialDataId: '',
    initialDataLoaded: false,
    loadingData: false,
    tabSourceId: '',
  };

  public static readonly EMPTY_ATTRIBUTE_LIST_DATA: AttributeListDataModel = {
    id: '',
    tabId: '',
    columns: [],
    rows: [],
    checkedRows: [],
    pageIndex: 1,
    pageSize: 100,
    totalCount: null,
    sortDirection: '',
  };

  private destroyed = new Subject();

  constructor() {
    combineLatest([
      this.tabsFromSources$,
      this.store$.select(selectAttributeListVisible),
    ])
      .pipe(
        takeUntil(this.destroyed),
        filter(([ _tabSources, attributeListVisible ]) => attributeListVisible),
        map(([ tabSources, _attributeListVisible ]) => tabSources),
        withLatestFrom(this.store$.select(selectAttributeListTabs)),
        concatMap(([ tabSources, tabs ]) => {
          const closedTabs = this.getClosedTabs(tabSources, tabs);
          const newTabs$ = this.getNewTabs$(tabSources, tabs);
          return forkJoin([ of(closedTabs), newTabs$ ]);
        }),
        filter(([ closedTabs, newTabs ]) => closedTabs.length > 0 || newTabs.length > 0),
      )
      .subscribe(([ closedTabs, newTabs ]) => {
        this.store$.dispatch(changeAttributeListTabs({
          newTabs: newTabs.map(result => result.tab),
          newData: newTabs.reduce<AttributeListDataModel[]>((data, result) => data.concat(...result.data), []),
          closedTabs,
        }));
      });
  }

  public ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public isLoadingTabs$(): Observable<boolean> {
    return this.sources$.asObservable().pipe(switchMap(sources => {
      if (sources.length === 0) {
        return of(false);
      }
      const loadingTabsObservables$ = sources.map(s => s.isLoadingTabs$ ?? of(false));
      return combineLatest(loadingTabsObservables$).pipe(map(s => {
        return s.some(isLoading => isLoading);
      }));
    }));
  }

  public getFeatures$(tabSourceId: string, params: GetFeaturesParams): Observable<FeaturesResponseModel> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of({ features: [], columnMetadata: [], total: null, page: null, pageSize: null, template: null });
    }
    return source.dataLoader.getFeatures$(params);
  }

  public getLayerExtractCapabilities$(tabSourceId: string, params: GetLayerExtractCapabilitiesParams): Observable<LayerExtractCapabilitiesModel> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of({ outputFormats: [] });
    }
    return source.dataLoader.getLayerExtractCapabilities$(params);
  }

  public startLayerExtract$(tabSourceId: string, params: GetLayerExtractParams): Observable<LayerExtractResponseModel | DownloadLayerExtractResponse | null> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of(null);
    }
    return source.dataLoader.startLayerExtract$(params);
  }

  public downloadLayerExtract$(tabSourceId: string, params: DownloadLayerExtractParams): Observable<DownloadLayerExtractResponse | null> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of(null);
    }
    return source.dataLoader.downloadLayerExtract$(params);
  }

  public getUniqueValues$(tabSourceId: string, params: GetUniqueValuesParams): Observable<UniqueValuesResponseModel> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of({ values: [], filterApplied: false });
    }
    return source.dataLoader.getUniqueValues$(params);
  }

  public canExpandRow$(tabSourceId: string, params: CanExpandRowParams): Observable<boolean> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of(false);
    }
    return source.dataLoader.canExpandRow$ ? source.dataLoader.canExpandRow$(params) : of(false);
  }

  public getFeatureDetails$(tabSourceId: string, params: GetFeatureDetailsParams): Observable<FeatureDetailsModel | null> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source || !source.dataLoader.getFeatureDetails$) {
      return of(null);
    }
    return source.dataLoader.getFeatureDetails$(params);
  }

  public canLoadStatistics(tabSourceId: string): boolean {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    return typeof source?.dataLoader.getStatisticValue$ === 'function';
  }

  public getStatistic$(tabSourceId: string, params: GetStatisticParams): Observable<GetStatisticResponse> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source || typeof source?.dataLoader.getStatisticValue$ !== 'function') {
      return of({ result: [], success: false });
    }
    return source.dataLoader.getStatisticValue$(params);
  }

  public retrieveZoomToExtentBounds$(tabSourceId: string, params: ZoomToExtentBoundsParams): Observable<BoundsModel | null> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source || typeof source?.dataLoader.retrieveZoomToExtentBounds$ !== 'function') {
      return of(null);
    }
    return source.dataLoader.retrieveZoomToExtentBounds$(params);
  }

  public canZoomToBounds(tabSourceId: string): boolean {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    return typeof source?.dataLoader.retrieveZoomToExtentBounds$ === 'function';
  }

  public canCheckRows$(tabSourceId: string, params: CanCheckRowsParams): Observable<boolean> {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    if (!source) {
      return of(false);
    }
    return source.dataLoader.canCheckRows$ ? source.dataLoader.canCheckRows$(params) : of(false);
  }

  public notifyCheckedRowsChanged(tabSourceId: string, params: CheckedRowsChangedParams): void {
    const source = this.sources$.getValue().find(s => s.id === tabSourceId);
    source?.dataLoader.onCheckedRowsChanged?.(params);
  }

  public addAttributeListSource(source: AttributeListSourceModel): void {
    this.sources$.next([
      ...this.sources$.getValue(),
      source,
    ]);
  }

  private getClosedTabs(visibleTabs: TabModel[], currentTabs: AttributeListTabModel[]): string[] {
    if (!currentTabs || currentTabs.length === 0) {
      return [];
    }
    return currentTabs
      .filter(tab => visibleTabs.findIndex(l => l.id === tab.layerId) === -1)
      .map<string>(tab => tab.id);
  }

  private getNewTabs$(
    visibleTabs: TabModelWithTabSourceId[],
    currentTabs: AttributeListTabModel[],
  ): Observable<TabFromLayerResult[]> {
    if (!visibleTabs || visibleTabs.length === 0) {
      return of([]);
    }
    const layersWithoutTab = visibleTabs.filter(layer => currentTabs.findIndex(t => t.layerId === layer.id) === -1);
    if (layersWithoutTab.length === 0) {
      return of([]);
    }
    return forkJoin(layersWithoutTab.map<Observable<TabFromLayerResult>>(layer => {
      return this.createTabFromModel$(layer, DEFAULT_ATTRIBUTE_LIST_CONFIG.pageSize);
    }));
  }

  private createTabFromModel$(
    tabModel: TabModelWithTabSourceId,
    pageSize = 10,
  ): Observable<TabFromLayerResult> {
      const id = nanoid();
      const dataId = nanoid();
      const tab: AttributeListTabModel = {
        ...AttributeListManagerService.EMPTY_ATTRIBUTE_LIST_TAB,
        id,
        layerId: tabModel.id,
        label: tabModel.label,
        selectedDataId: dataId,
        initialDataId: dataId,
        tabSourceId: tabModel.tabSourceId,
      };
      const data: AttributeListDataModel = {
        ...AttributeListManagerService.EMPTY_ATTRIBUTE_LIST_DATA,
        id: dataId,
        tabId: id,
        pageSize,
      };
      return of({ tab, data: [data] });
  }

}
